/**
 * Demo missions for the run page's phases that the rest of the demo data does not reach (?mock=1):
 * one still preparing, one whose branch the landing queue is checking, one landed. They reuse the
 * shared demo sessions (lib/mock.ts), and their landings live here, not in the Pipelines demo
 * data, so that page reads as before (run/data.ts adds them in demo mode).
 */
import { normalizeCriteria } from '@orgus/protocol';
import type { BacklogItem, Landing, LandingEvent, Mission, MissionLaunch } from '@orgus/protocol';
import { MOCK_IDS } from '../../../lib/mock.ts';

const S = 1000;
const MIN = 60 * S;
const H = 60 * MIN;
const M = MOCK_IDS;

export const RUN_DEMO = {
  preparing: 'msn_demo_trace',
  working: 'msn_demo_sessions_page',
  checking: 'msn_demo_voice_config',
  landed: 'msn_demo_stripe_bump',
  checkFailed: 'msn_demo_guide_links',
  checkingItem: 'itm_demo_voice_config',
  checkingSession: '5c2b8e4d-voice-latency',
  landedSession: '1f6e2a9c-nightly-deps',
  workingSession: 'f0c9e1a2-sessions-page',
} as const;

function launch(machineId: string, at: number, sessionId: string | null, extra: Partial<MissionLaunch> = {}): MissionLaunch {
  return { id: `au_run_${sessionId ?? at}`, machineId, at, by: 'owner', mode: 'headless', model: 'claude-opus-5', checkoutId: null, cwd: null, worktree: null, sessionId, result: 'ok', ...extra };
}

function mission(id: string, projectId: string, itemId: string | null, title: string, goal: string, now: number, extra: Partial<Mission>): Mission {
  return {
    id,
    itemId,
    projectId,
    title,
    goal,
    owner: 'owner',
    state: 'planned',
    progress: 0,
    sessionIds: [],
    launches: [],
    placement: {},
    model: null,
    budget: null,
    closed: null,
    syncedState: null,
    report: null,
    sequenceId: null,
    createdAt: now - 6 * H,
    updatedAt: now - MIN,
    ...extra,
  };
}

/** The items the run demo needs that the backlog demo does not have. */
export function runDemoItems(now: number): BacklogItem[] {
  return [
    {
      id: RUN_DEMO.checkingItem,
      key: 'QAWL-12',
      projectId: M.qawl,
      kind: 'task',
      title: 'Fetch the voice config once per call',
      body: 'The mint waits on a config fetch every time. Fetch it once when the call starts and reuse it.',
      status: 'in_progress',
      priority: 1,
      owner: 'srikanth',
      parentId: 'itm_demo_voice',
      rank: 1000,
      estimate: 2,
      labels: [],
      components: [],
      defect: null,
      source: null,
      aiDrafted: false,
      statusNote: null,
      statusPinned: false,
      createdAt: now - 8 * H,
      updatedAt: now - 2 * MIN,
      acceptance: normalizeCriteria([
        { format: 'gwt', text: '', given: 'a new voice call', when: 'the token is minted', then: 'the config is fetched once, not per mint', done: false },
        { format: 'check', text: 'Minting takes under 1 s at p95 in the latency probe', done: false },
      ]),
    },
  ];
}

export function runDemoMissions(now: number): Mission[] {
  return [
    // Preparing: the start reached studio 20 s ago; the session has not reported yet.
    mission(RUN_DEMO.preparing, M.orgus, null, 'Keep source sections on imported items', 'Every item made by an import keeps the document sections it came from, and the drawer links to them.', now, {
      launches: [launch(M.studio, now - 20 * S, null, { branch: 'air-5-trace-sections', worktree: 'C:/Users/Srika/CascadeProjects/dev-portal-wt/air-5-trace-sections' })],
      placement: { machineId: M.studio },
      model: 'claude-opus-5',
      budget: 15,
      updatedAt: now - 20 * S,
    }),
    // Working: the sessions page is being built; nothing waits on a person.
    mission(RUN_DEMO.working, M.orgus, null, 'Sessions page: grouped list, filters and the drawer', 'Sessions group by state, filter by project and machine, and open in a drawer with the live transcript.', now, {
      sessionIds: [RUN_DEMO.workingSession],
      launches: [launch(M.studio, now - 26 * MIN, RUN_DEMO.workingSession, { checkoutId: 'c-fd-main', branch: 'air-8-sessions-page', model: 'claude-opus-5' })],
      placement: { machineId: M.studio },
      model: 'claude-opus-5',
      budget: 12,
    }),
    // Checking: the session finished and submitted its branch; the landing queue verifies it now.
    mission(RUN_DEMO.checking, M.qawl, RUN_DEMO.checkingItem, 'Fetch the voice config once per call', 'The token mint reuses the call config, and minting takes under 1 s at p95.', now, {
      sessionIds: [RUN_DEMO.checkingSession],
      launches: [launch(M.mbp, now - 7 * H, RUN_DEMO.checkingSession, { checkoutId: 'c-qawl-mbp', branch: 'qawl-12-voice-config', model: 'claude-opus-5' })],
      placement: { machineId: M.mbp },
      model: 'claude-opus-5',
      budget: 20,
      report: {
        at: now - 5 * H,
        text: 'ACCEPTANCE REPORT\nAC-1: PASS - the config is fetched in startCall and passed to mint\nAC-2: PASS - the probe reads 640 ms at p95',
        acceptance: [
          { id: 'ac1', met: true, evidence: 'the config is fetched in startCall and passed to mint' },
          { id: 'ac2', met: true, evidence: 'the probe reads 640 ms at p95' },
        ],
      },
    }),
    // Failed checks: the guide's branch went to the queue and its build found a broken link.
    mission(RUN_DEMO.checkFailed, M.docs, 'itm_demo_guide', 'Fix the links in the v2 guide', 'Every link in the getting started guide resolves, and the docs build passes.', now, {
      sessionIds: ['2e8f7a91-docs-v2'],
      launches: [launch(M.mbp, now - 5 * H, '2e8f7a91-docs-v2', { checkoutId: 'c-docs-mbp', branch: 'docs-1-guide-links', model: 'claude-sonnet-5' })],
      placement: { machineId: M.mbp },
      model: 'claude-sonnet-5',
      budget: 10,
    }),
    // Landed: the nightly upgrade went through the queue and is on main.
    mission(RUN_DEMO.landed, M.billing, 'itm_demo_deps', 'Bump stripe to 18.2', 'Dependencies are on their latest minor versions and the tests pass.', now, {
      sessionIds: [RUN_DEMO.landedSession],
      launches: [launch(M.build, now - 4 * H, RUN_DEMO.landedSession, { checkoutId: 'c-bill-build', branch: 'chore/stripe-18', model: 'claude-haiku-5-5' })],
      placement: { machineId: M.build },
      model: 'claude-haiku-5-5',
      budget: 5,
      landed: { branch: 'chore/stripe-18', sha: '4be3c1f0a9d27e5b8c6f1d0e2a3b4c5d6e7f8091', sessionId: RUN_DEMO.landedSession, at: now - 3 * H, by: 'orgus' },
      closed: { as: 'done', at: now - 3 * H, by: 'orgus', note: 'Landed chore/stripe-18 on main at 4be3c1f0a9d2.' },
      syncedState: 'done',
      updatedAt: now - 3 * H,
    }),
  ];
}

function ev(now: number, ago: number, stage: LandingEvent['stage'], text: string, tone: LandingEvent['tone'] = 'info', machineId?: string): LandingEvent {
  return { at: now - ago, stage, text, tone, ...(machineId ? { machineId } : {}) };
}

/** The demo missions' landings: one verifying now, one failed, one landed. */
export function demoMissionLandings(now: number): Landing[] {
  const base = {
    target: 'main',
    conflicts: 0,
    baseSha: '9a1c3e5f7b2d4f6a8c0e1b3d5f7a9c1e3b5d7f9a',
    version: null,
    deploymentIds: [],
  };
  return [
    {
      ...base,
      id: 'lnd_run_voice',
      queueId: `${M.qawl}:main`,
      projectId: M.qawl,
      title: 'Fetch the voice config once per call',
      source: { branch: 'qawl-12-voice-config', machineId: M.mbp },
      submittedBy: 'agent:mission',
      submittedAt: now - 3 * MIN,
      missionId: RUN_DEMO.checking,
      sessionId: RUN_DEMO.checkingSession,
      status: 'active',
      stage: 'verify',
      step: 'Running pnpm test on mbp-16',
      machineId: M.mbp,
      jobId: 'j-run-voice',
      attempts: 1,
      headSha: null,
      landedSha: null,
      startedAt: now - 2 * MIN,
      endedAt: null,
      updatedAt: now - 5 * S,
      events: [
        ev(now, 3 * MIN, 'change', 'Submitted by the mission'),
        ev(now, 2 * MIN, 'verify', 'Rebasing qawl-12-voice-config onto main in a fresh checkout on mbp-16', 'info', M.mbp),
        ev(now, 2 * MIN - 9 * S, 'verify', 'Rebased 3 commits onto 9a1c3e5', 'ok', M.mbp),
        ev(now, 2 * MIN - 10 * S, 'verify', 'Running pnpm test on mbp-16', 'info', M.mbp),
      ],
    },
    {
      ...base,
      id: 'lnd_run_guide',
      queueId: `${M.docs}:main`,
      projectId: M.docs,
      title: 'Fix the links in the v2 guide',
      source: { branch: 'docs-1-guide-links', machineId: M.mbp },
      submittedBy: 'agent:mission',
      submittedAt: now - 2 * H - 20 * MIN,
      missionId: RUN_DEMO.checkFailed,
      sessionId: '2e8f7a91-docs-v2',
      status: 'failed',
      stage: 'verify',
      step: 'Verify failed.',
      machineId: M.mbp,
      jobId: 'j-run-guide',
      attempts: 1,
      headSha: null,
      landedSha: null,
      startedAt: now - 2 * H - 18 * MIN,
      endedAt: now - 2 * H - 14 * MIN,
      updatedAt: now - 2 * H - 14 * MIN,
      verifyMs: 4 * MIN,
      problem: { what: 'Verify failed: npm run build.', why: 'It exited with code 1 on mbp-16: a broken link to /docs/install#windows.', next: 'Fix the link on the branch, push it, then retry the checks.' },
      events: [
        ev(now, 2 * H + 20 * MIN, 'change', 'Submitted by the mission'),
        ev(now, 2 * H + 18 * MIN, 'verify', 'Running npm run build on mbp-16', 'info', M.mbp),
        ev(now, 2 * H + 14 * MIN, 'verify', 'npm run build exited with code 1: broken link /docs/install#windows', 'error', M.mbp),
      ],
    },
    {
      ...base,
      id: 'lnd_run_stripe',
      queueId: `${M.billing}:main`,
      projectId: M.billing,
      title: 'Bump stripe to 18.2',
      source: { branch: 'chore/stripe-18', machineId: M.build },
      submittedBy: 'agent:mission',
      submittedAt: now - 3 * H - 9 * MIN,
      missionId: RUN_DEMO.landed,
      sessionId: RUN_DEMO.landedSession,
      status: 'landed',
      stage: 'landed',
      step: 'Landed on main',
      machineId: M.build,
      jobId: 'j-run-stripe',
      attempts: 1,
      headSha: '4be3c1f0a9d27e5b8c6f1d0e2a3b4c5d6e7f8091',
      landedSha: '4be3c1f0a9d27e5b8c6f1d0e2a3b4c5d6e7f8091',
      version: '4be3c1f',
      startedAt: now - 3 * H - 8 * MIN,
      endedAt: now - 3 * H,
      updatedAt: now - 3 * H,
      verifyMs: 6 * MIN + 12 * S,
      events: [
        ev(now, 3 * H + 9 * MIN, 'change', 'Submitted by the mission'),
        ev(now, 3 * H + 8 * MIN, 'verify', 'Running npm test on build-01', 'info', M.build),
        ev(now, 3 * H + 2 * MIN, 'verify', 'Verify passed in 6 min 12 s', 'ok', M.build),
        ev(now, 3 * H + MIN, 'push', 'Pushed to main as 4be3c1f', 'ok'),
        ev(now, 3 * H, 'landed', 'Landed on main as 4be3c1f', 'ok'),
      ],
    },
  ];
}

/**
 * The review demo: the $0 pricing fix finished on its own branch and reported on each criterion,
 * one not met, so Accept and land, Send back and Split follow-up all have something to act on.
 */
export function runDemoReview(items: BacklogItem[], missions: Mission[], now: number): void {
  const it = items.find((i) => i.id === 'itm_demo_pricing');
  if (it) {
    it.acceptance = normalizeCriteria([
      { format: 'check', text: 'A finished run is priced from its usage rows', done: false },
      { format: 'check', text: "Last week's runs are re-priced", done: false },
      { format: 'check', text: 'Spend shows the backfilled total for last week', done: false },
    ]);
  }
  const m = missions.find((x) => x.id === 'msn_demo_pricing');
  if (m) {
    m.launches = m.launches.map((l) => ({ ...l, checkoutId: 'c-qawl-main', branch: 'qawl-9-run-pricing' }));
    m.budget = 30;
    m.report = {
      at: now - 25 * H,
      // ADR 0078: a criterion it reported failed would make the mission failed; one it does not
      // cover leaves it In review for a person, which is what this demo shows.
      text: 'Spend reads the old daily cache until it rebuilds at midnight, so I left that criterion for review.\nACCEPTANCE REPORT\nAC-1: PASS - pricing.test.ts prices a run from its usage rows\nAC-2: PASS - 1,912 runs from last week re-priced',
      acceptance: [
        { id: 'ac1', met: true, evidence: 'pricing.test.ts prices a run from its usage rows' },
        { id: 'ac2', met: true, evidence: '1,912 runs from last week re-priced' },
      ],
    };
  }
}
